import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BEAAttribution } from '../components/BEAAttribution';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface RimsDataset {
  id: string;
  regionName: string;
  regionType: string;
  multiplierType: string;
  ioVintageYear: number | null;
  regionalVintageYear: number | null;
  sourceFilename: string;
  status: 'uploaded' | 'parsing' | 'ready' | 'failed';
  errorMessage: string | null;
  rowCount: number | null;
  createdAt: string;
}

type UploadState =
  | { type: 'idle' }
  | { type: 'uploading' }
  | { type: 'success'; datasetId: string }
  | { type: 'error'; message: string };

function StatusBadge({ status }: { status: RimsDataset['status'] }) {
  const styles: Record<RimsDataset['status'], string> = {
    uploaded: 'bg-slate-100 text-slate-600',
    parsing: 'bg-amber-50 text-amber-700',
    ready: 'bg-green-50 text-green-700',
    failed: 'bg-red-50 text-red-700',
  };
  const labels: Record<RimsDataset['status'], string> = {
    uploaded: 'Queued',
    parsing: 'Parsing…',
    ready: 'Ready',
    failed: 'Failed',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

export function SettingsRimsRoute() {
  const getToken = useSessionTokenGetter();
  const [datasets, setDatasets] = useState<RimsDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState<UploadState>({ type: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDatasets = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API_URL}/api/v1/rims-datasets`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const body = await res.json();
    setDatasets((body.datasets ?? []).slice().reverse()); // newest first
    setLoading(false);
  }, [getToken]);

  useEffect(() => {
    void fetchDatasets();
    // Poll while any dataset is in a transient state (uploaded / parsing)
    const id = setInterval(() => {
      void fetchDatasets();
    }, 5000);
    return () => clearInterval(id);
  }, [fetchDatasets]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset the input so the same file can be re-selected after an error
      if (fileInputRef.current) fileInputRef.current.value = '';

      setUpload({ type: 'uploading' });

      try {
        const token = await getToken();
        const form = new FormData();
        form.append('file', file);

        const res = await fetch(`${API_URL}/api/v1/rims-datasets`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });

        const body = await res.json();

        if (!res.ok) {
          setUpload({ type: 'error', message: body.error ?? `Upload failed (${res.status})` });
          return;
        }

        setUpload({ type: 'success', datasetId: body.dataset.id });
        await fetchDatasets();
      } catch (err) {
        setUpload({
          type: 'error',
          message: err instanceof Error ? err.message : 'Upload failed',
        });
      }
    },
    [getToken, fetchDatasets],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const token = await getToken();
      await fetch(`${API_URL}/api/v1/rims-datasets/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDatasets();
    },
    [getToken, fetchDatasets],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">RIMS II Multipliers</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload the BEA "Table 1.5 Total Multipliers" xlsx file for your region. The file is parsed
          into per-industry multipliers used in every analysis.
        </p>
        <BEAAttribution variant="rims" className="mt-2" />
      </div>

      {/* Upload card */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Upload new dataset
        </h3>

        <div className="mt-4 flex items-center gap-4">
          <label
            htmlFor="rims-upload"
            className={`cursor-pointer rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
              upload.type === 'uploading'
                ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {upload.type === 'uploading' ? 'Uploading…' : 'Choose .xlsx file'}
          </label>
          <input
            id="rims-upload"
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            disabled={upload.type === 'uploading'}
            onChange={handleFileChange}
          />

          {upload.type === 'success' && (
            <span className="text-sm text-green-600">Uploaded — parsing in the background.</span>
          )}
          {upload.type === 'error' && (
            <span className="text-sm text-red-600">{upload.message}</span>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-400">
          Accepts BEA Table 1.5 Total Multipliers (Type II). Max 10 MB.
        </p>
      </div>

      {/* Dataset list */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Uploaded datasets
          </h3>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : datasets.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400">
            No datasets uploaded yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {datasets.map((ds) => (
              <li key={ds.id} className="flex items-center justify-between px-6 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-800">
                      {ds.regionName}
                    </span>
                    <StatusBadge status={ds.status} />
                  </div>
                  <div className="mt-0.5 flex gap-3 text-xs text-slate-400">
                    <span>{ds.sourceFilename}</span>
                    {ds.rowCount != null && <span>{ds.rowCount.toLocaleString()} industries</span>}
                    {ds.multiplierType === 'type_ii' && <span>Type II</span>}
                    {ds.ioVintageYear && (
                      <span>
                        {ds.ioVintageYear} I/O · {ds.regionalVintageYear} regional
                      </span>
                    )}
                  </div>
                  {ds.status === 'failed' && ds.errorMessage && (
                    <p className="mt-1 text-xs text-red-600">{ds.errorMessage}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void handleDelete(ds.id)}
                  className="ml-4 shrink-0 rounded-md px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-100 hover:text-red-600"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
